import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Button } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { pick } from 'underscore';

import t from 'react-translate';

import { addAlertMessage } from 'redux/actions/alert-messages';
import { openConfirmationDialog } from 'redux/actions/confirmation-dialogs';
import {
  updateAccredibleGroupId,
  removeAccredibleFromCourse,
  getAccredibleGroup,
  validateAndSaveAccredibleGroupId,
} from 'redux/actions/digital-credential';
import { AlertMessageType } from 'redux/schemas/app/alert-message';
import { Institution } from 'redux/schemas/models/institution';
import { getCourseAliases, getCurrentCourse } from 'redux/selectors/course';
import { getCurrentInstitution } from 'redux/selectors/institutions';
import { useAppDispatch } from 'redux/store';
import { AccredibleGroupInfo } from 'redux/schemas/models/credly';

import NvTextInput from 'shared/components/inputs/nv-text-input';
import NvModal, { ModalType } from 'shared/components/nv-modal';
import CredentialBadgeCard from 'shared/components/credential-badge-card';
import { config } from '@config/pendo.config.json';
import { extractTextFromHTML } from 'shared/utils';

type AccredibleSettingsProps = {
  show: boolean;
  onClose: () => void;
};

const AccredibleSettings = ({ onClose }: { onClose: () => void }) => {
  const dispatch = useAppDispatch();
  const institution: Institution = useSelector(getCurrentInstitution);
  const currentCourse = useSelector(getCurrentCourse);
  const catalogId = useSelector(state => state.app.currentCatalogId);
  const aliases = useSelector(state => getCourseAliases(state));

  const existingGroupId = currentCourse?.accredibleGroupId;
  const groupInfo = currentCourse?.accredibleGroupInfo;

  const [groupId, setGroupId] = useState(existingGroupId ?? '');
  const [isValidating, setIsValidating] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationResult, setValidationResult] = useState<'success' | 'error' | null>(null);
  const [validatedGroupInfo, setValidatedGroupInfo] = useState<AccredibleGroupInfo | null>(null);
  const [lastValidatedGroupId, setLastValidatedGroupId] = useState<string | null>(null);

  const hasFetchedInitialRef = useRef(false);

  // Use validatedGroupInfo for preview during validation, fall back to Redux groupInfo
  const displayGroupInfo = validatedGroupInfo || groupInfo;

  const fetchGroupDetails = useCallback(async (id: string) => {
    const result = await dispatch(
      getAccredibleGroup({
        institutionId: institution.id,
        groupId: id,
      }),
    ).unwrap();
    setValidatedGroupInfo(result);
  }, [dispatch, institution.id]);

  // Load existing group details on mount only if not already in Redux
  useEffect(() => {
    if (existingGroupId && !groupInfo && !hasFetchedInitialRef.current) {
      hasFetchedInitialRef.current = true;
      fetchGroupDetails(existingGroupId).catch(() => {
        dispatch(addAlertMessage({
          type: AlertMessageType.ERROR,
          header: t.FORM.OOPS(),
          message: t.INSTITUTIONS.ADVANCED_SETTINGS.ACCREDIBLE.COURSE.GROUP_NOT_FOUND(),
        }));
      });
    }
  }, [existingGroupId, groupInfo, fetchGroupDetails, dispatch]);

  const handleValidate = async () => {
    setIsValidating(true);
    setValidationResult(null);
    const trimmedGroupId = groupId.trim();

    try {
      await fetchGroupDetails(trimmedGroupId);
      setValidationResult('success');
      setLastValidatedGroupId(trimmedGroupId);
    } catch {
      setValidationResult('error');
    } finally {
      setIsValidating(false);
    }
  };

  const resetForm = () => {
    setValidatedGroupInfo(null);
    setGroupId('');
    setValidationResult(null);
    setLastValidatedGroupId(existingGroupId ?? null);
  };

  const handleClose = () => {
    setValidationResult(null);
    setValidatedGroupInfo(null);
    onClose();
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);

    const trimmedGroupId = groupId.trim();
    const payload = { institutionId: institution.id, catalogId, groupId: trimmedGroupId };
    const action = existingGroupId
      ? updateAccredibleGroupId(payload)
      : validateAndSaveAccredibleGroupId(payload);

    try {
      await dispatch(action).unwrap();
      dispatch(
        addAlertMessage({
          type: AlertMessageType.SUCCESS,
          header: t.FORM.SUCCESS_BANG(),
          message: existingGroupId
            ? t.INSTITUTIONS.ADVANCED_SETTINGS.ACCREDIBLE.COURSE.UPDATE_SUCCESS()
            : t.INSTITUTIONS.ADVANCED_SETTINGS.ACCREDIBLE.COURSE.ENABLE_SUCCESS({ ...aliases.courseAliases }),
        }),
      );
      handleClose();
    } catch {
      // Error alert handled by action
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRemove = async () => {
    if (!existingGroupId) {
      resetForm();
      return;
    }

    dispatch(
      openConfirmationDialog({
        title: t.FORM.ARE_YOU_SURE(),
        bodyText: t.INSTITUTIONS.ADVANCED_SETTINGS.ACCREDIBLE.COURSE.REMOVE_DESCRIPTION({ ...aliases.learnersAliases }),
        confirmText: t.FORM.YES_SURE(),
        cancelText: t.FORM.CANCEL(),
        onConfirm: async () => {
          try {
            await dispatch(
              removeAccredibleFromCourse({
                institutionId: institution.id,
                catalogId,
              }),
            ).unwrap();
            handleClose();
          } catch {
            // Error handled by action
          }
        },
        confirmDataQa: config.pendo.accredible.disableConfirm,
        cancelDataQa: config.pendo.accredible.disableCancel,
      }),
    );
  };

  const handleGroupIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setGroupId(newValue);
    // Restore 'success' if input matches last validated groupId
    if (newValue.trim() === lastValidatedGroupId) {
      setValidationResult('success');
    } else if (validationResult) {
      setValidationResult(null);
    }
  };

  const isValidated = validationResult === 'success';

  // Input needs (re)validation when:
  // - Not currently validated successfully, AND
  // - Either no existing groupId (initial setup) or input differs from existing
  const needsRevalidation = !isValidated && (
    !existingGroupId || groupId.trim() !== existingGroupId
  );

  const isValidateDisabled = !groupId.trim() || isValidating || !needsRevalidation;
  const isSubmitDisabled = !isValidated || isSubmitting;
  const isBadgeSectionDisabled = isValidating || needsRevalidation;

  const getSubmitButtonText = () => {
    if (isSubmitting) return existingGroupId ? t.FORM.UPDATING() : t.FORM.SUBMITTING();
    return existingGroupId ? t.FORM.UPDATE() : t.FORM.SUBMIT();
  };

  const getValidationMessage = () => (
    isValidated
      ? t.INSTITUTIONS.ADVANCED_SETTINGS.ACCREDIBLE.COURSE.GROUP_FOUND()
      : t.INSTITUTIONS.ADVANCED_SETTINGS.ACCREDIBLE.COURSE.GROUP_NOT_FOUND()
  );

  return (
    <div className='d-flex flex-column p-4 text-body'>
      {t.INSTITUTIONS.ADVANCED_SETTINGS.ACCREDIBLE.COURSE.DESCRIPTION({ ...aliases.learnersAliases })}
      <div className='mt-4'>
        <label
          htmlFor='accredible-group-id'
          className='text-gray-3 mb-0 mr-2'
        >
          {t.INSTITUTIONS.ADVANCED_SETTINGS.ACCREDIBLE.COURSE.GROUP_ID()}
        </label>
        <div className='d-flex align-items-center'>
          <NvTextInput
            id='accredible-group-id'
            value={groupId}
            onChange={handleGroupIdChange}
            placeholder={t.INSTITUTIONS.ADVANCED_SETTINGS.ACCREDIBLE.COURSE.GROUP_ID()}
            required
            className='w-100'
            data-qa={config.pendo.accredible.groupId}
          />
          {validationResult ? (
            <span className={`text-small text-nowrap bold ml-2 ${isValidated ? 'text-success' : 'text-danger'}`}>
              {getValidationMessage()}
            </span>
          ) : (
            <Button
              className='ml-2'
              variant='primary'
              disabled={isValidateDisabled}
              onClick={handleValidate}
              data-qa={config.pendo.accredible.badgeValidate}
            >
              {isValidating ? t.FORM.VALIDATING() : t.FORM.VALIDATE()}
            </Button>
          )}
        </div>
      </div>
      {displayGroupInfo && (
        <div className='mt-4'>
          <CredentialBadgeCard
            badgeInfo={{
              imageUrl: displayGroupInfo.imageUrl,
              name: displayGroupInfo.name,
              description: displayGroupInfo.description ? extractTextFromHTML(displayGroupInfo.description) : '',
              url: displayGroupInfo.groupUrl,
            }}
            viewLinkText={t.INSTITUTIONS.ADVANCED_SETTINGS.ACCREDIBLE.COURSE.VIEW_IN_ACCREDIBLE()}
            removeLinkText={t.INSTITUTIONS.ADVANCED_SETTINGS.ACCREDIBLE.COURSE.REMOVE_FROM_COURSE({ ...aliases.courseAliases })}
            onRemove={handleRemove}
            disabled={isBadgeSectionDisabled}
            showRemove
            dataQas={pick(config.pendo.accredible, 'badgeUrl', 'badgeRemove')}
          />
        </div>
      )}
      <div className='d-flex justify-content-center mt-7 p-3'>
        <Button
          variant='secondary'
          onClick={handleClose}
          className='mr-2'
          data-qa={config.pendo.accredible.badgeCancel}
        >
          {t.FORM.CANCEL()}
        </Button>
        <Button
          variant='primary'
          disabled={isSubmitDisabled}
          onClick={handleSubmit}
          data-qa={config.pendo.accredible.badgeSubmit}
        >
          {getSubmitButtonText()}
        </Button>
      </div>
    </div>
  );
};

export const AccredibleSettingsModal = ({ show, onClose }: AccredibleSettingsProps) => (
  <NvModal
    show={show}
    height='auto'
    type={ModalType.FIXED}
    onClose={onClose}
    header={t.INSTITUTIONS.ADVANCED_SETTINGS.ACCREDIBLE.TITLE()}
    width={660}
    body={(
      <AccredibleSettings
        onClose={onClose}
      />
    )}
  />
);

export default AccredibleSettingsModal;
